import * as Sentry from '@sentry/react';
import { useMutation } from '@do/graphql-client';
import { Checkbox, Text as WalrusText } from '@do/walrus';
import { Form, Formik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PUBLIC_ENDPOINT } from '../constants';
import copy from '../copy';
import { verifyDevice as VerifyDevice, verifyDeviceVariables } from '../generated-types/verifyDevice';
import { VERIFY_DEVICE } from '../queries/queries';
import { addSessionData, handleFormError, saveSessionData } from '../utils/gql-utils';
import { validationCopy, yupVerificationNumberValidation } from '../utils/validation-utils';
import ClickLink from './ClickLink';
import DOLink from './DOLink';
import ErrorBanner from './ErrorBanner';
import SpinnerButton from './SpinnerButton';
import Text from './Text';
import TextInput from './TextInput';
import Title from './Title';

const VerificationValidation = Yup.object().shape({
  code: yupVerificationNumberValidation,
  trust_device: Yup.boolean(),
});

const VerificationCheckbox = styled(Checkbox)`
  && {
    text-transform: initial;
    letter-spacing: initial;
    font-size: inherit;
  }
`;
interface Props {
  goBack: () => void;
}

interface ErrorMessage {
  title?: string;
  details: string;
  requestId?: string;
}

interface FormValues {
  code: string;
  trust_device: boolean;
}

const VerificationForm: React.FC<Props> = ({ goBack }) => {
  const [genericServerError, setGenericServerError] = React.useState<ErrorMessage | null>(null);
  const [verifyDevice, { loading: isVerifyingDevice }] = useMutation<VerifyDevice, verifyDeviceVariables>(
    VERIFY_DEVICE,
    {
      context: {
        url: PUBLIC_ENDPOINT,
      },
    },
  );

  return (
    <>
      <Title title={copy.form.verification.title} />
      <Text>{copy.form.verification.details}</Text>

      <Formik<FormValues>
        validateOnBlur={false}
        initialValues={{
          code: '',
          trust_device: false,
        }}
        validationSchema={VerificationValidation}
        onSubmit={(values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          verifyDevice({
            variables: {
              verifyDeviceRequest: addSessionData(values),
            },
          })
            .then((successResponse) => {
              const { status, session_data: sessionData } = successResponse.data?.verifyDevice ?? {};
              const requestId = successResponse.data?.request_id;

              saveSessionData(sessionData);

              if (status === 'granted') {
                window.location.reload();
              } else if (status === 'expired') {
                setGenericServerError({
                  title: copy.errors.expired_verification_code.title,
                  details: copy.errors.expired_verification_code.details,
                  requestId,
                });
              } else if (status === 'rejected') {
                setGenericServerError({
                  title: copy.errors.invalid_verification_code.title,
                  details: copy.errors.invalid_verification_code.details,
                  requestId,
                });
              }
            })
            .catch((error) => {
              handleFormError(setErrors, setGenericServerError)(error);
              Sentry.captureException(error);
              console.error('Error in verifying device:', error);
            });
        }}
      >
        {({ errors, values, touched, setFieldValue }) => (
          <Form>
            {genericServerError && <ErrorBanner error={genericServerError} />}
            <TextInput
              label={validationCopy.verification.code_label}
              placeholder={`${copy.form.two_factor_auth.placeholder_prefix} ${validationCopy.verification.code_label}`}
              name="code"
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              touched={touched.code}
              error={errors.code}
              value={values.code}
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                setFieldValue('code', e.currentTarget?.value?.trim());
              }}
            />
            <VerificationCheckbox
              className="do-u-mt--small"
              name="trust_device"
              label={copy.form.verification.checkbox_label}
              checked={values.trust_device}
              error={errors.trust_device}
            />
            <SpinnerButton
              className="do-u-mt--small"
              type="submit"
              fullWidth
              spinning={isVerifyingDevice}
              disabled={isVerifyingDevice}
              loadingMessage="Verifying your device"
            >
              {copy.form.verification.button_text}
            </SpinnerButton>
          </Form>
        )}
      </Formik>
      <WalrusText type="p" className="need-help-text">
        {copy.form.verification.need_help_text}
      </WalrusText>
      <Text className="do-u-mt--small">
        {copy.form.verification.checkout_text}{' '}
        <DOLink path="/docs/getting-started/faq/#dve">{copy.form.verification.troubleshooting_link}</DOLink>
        <> {copy.form.or} </>
        <ClickLink onClick={goBack}>{copy.form.verification.try_again_link}</ClickLink>
      </Text>
    </>
  );
};

export default VerificationForm;
